/**
 * Acción de REDUX que permite llevar a cabo todas las operaciones sobre los clientes 
 */

import { CUSTOMERS, DOCUMENTS } from '../../../application/types';
import { seedMatrixFor } from '../../lib/role_seed';
import { applyUserScope } from '../../lib/user_scope';
import { toggleAdmin, adminsUidsOf } from '../../lib/customer_admin';
import { canAdjust, canToggleDelete } from '../../lib/rank';
import { loadTargetRole } from '../../lib/role_mirror';

/**
 * Rol global de quien opera, tal como lo guarda el reducer de roles
 * (state().get('roles').get('roles').roles). Sin rol legible → null.
 */
const callerRoleOf = (state) => {
  try {
    return state().get('roles').get('roles').roles || null;
  } catch (e) {
    return null;
  }
};

/**
 * Rol del usuario destino ya con su alcance (users/{uid}.Alcance) y su id,
 * para decidir la siembra por rol (lib/role_seed). Sin rol legible → id undefined (sin permisos).
 * Lo usan addUser/setUserAdmin y los drawers de permisos.
 */
export const loadScopedRole = async (db, uid) => {
  const u = await db.collection('users').doc(uid).get();
  const userData = u.exists ? (u.data() || {}) : {};
  const rolRef = userData.Rol;
  const rolSnap = rolRef && typeof rolRef.get === 'function' ? await rolRef.get() : null;
  const ok = !!(rolSnap && rolSnap.exists);
  const scoped = applyUserScope(ok ? (rolSnap.data() || {}) : {}, userData);
  // Rol desconocido: sin id → clase 'role' con permisos todo en false; nunca
  // la plantilla de cliente.
  return { ...scoped, id: ok ? rolRef.id : undefined };
};

/**
 * Exporta las funciones para el manejo de los clientes
 */
export const customers = {

  /**
   * Función que permite agregar usuario y vincularlos a un cliente.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * ID del cliente
   * Email del usuario que se va a vincular
   */
  addUser: ({ roles, id, email }) => ( dispatch, state, firebase ) => {
    
    dispatch({ type: CUSTOMERS.CREATE.CREATING_CUSTOMERS });

    return new Promise( ( resolve, reject ) => {
      if ( roles.create ) {
      
        const db = firebase().firestore();
        const user = db.collection('users').where( 'Email', '==', email );

        user.get().then( query => {
          if ( query.size > 0 ) {

            const userRef = query.docs[0].ref;
            const userData = (typeof query.docs[0].data === 'function' && query.docs[0].data()) || {};
            const customer = db.collection('customers').doc( id );

            const minimalMatrixRef = db.doc(`folders/${ id }/users/${ userRef.id }`);
            const rolRef = userData.Rol;
            const rolPromise = rolRef && typeof rolRef.get === 'function' ? rolRef.get() : Promise.resolve(null);

            // Jerarquía (enmienda E2): solo se vincula a alguien de rango menor. El rol
            // del objetivo sale del espejo público; ilegible → no se vincula.
            loadTargetRole({ db, uid: userRef.id }).then( targetRole => {
              const deny = ( code, message ) => {
                dispatch({ type: CUSTOMERS.UPDATE.ERROR, code, message });
                return Promise.reject( Object.assign( new Error( message ), { code, handled: true } ) );
              };
              if ( !targetRole ) return deny( 'role-unreadable', 'No se pudo leer el rol de este usuario; no se puede vincular.' );
              if ( !canAdjust( callerRoleOf( state ), targetRole ) ) return deny( 'rank', 'No puedes vincular a alguien de tu mismo rango o superior' );
              return Promise.all([ customer.get(), minimalMatrixRef.get(), rolPromise ]);
            }).then( ([ query, matrixSnap, rolSnap ]) => {
              // Sin rol válido no se vincula: no hay con qué decidir la siembra.
              if (!(rolSnap && rolSnap.exists)) {
                return reject(
                  dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: 'role', message: 'El usuario no tiene un rol válido; asígnele uno antes de vincularlo.' })
                );
              }
              if (!(query.data()?.['Usuarios']?.filter(user => user.email === email).length > 0)) {
                const uid = firebase().auth().currentUser.uid;
                const batch = db.batch();

                const log_audit = db.collection('audit').doc();
                const users = query.data()?.['Usuarios'] || [];

                users.push({ email: email, reference: userRef })

                const before = { ...query.data() };
                const after = { ...query.data(), 'Usuarios': users };
                
                batch.set(log_audit, { uid, eventType: CUSTOMERS.UPDATE.UPDATE_CUSTOMERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
                batch.set(log_audit.collection('before').doc(), before);
                batch.set(log_audit.collection('after').doc(), after);
                batch.update(customer, {
                  'Usuarios': firebase().firestore.FieldValue.arrayUnion({
                    email: email,
                    reference: userRef
                  }),
                  // Array plano para consultar por uid (spec 2026-10-05).
                  'UsuariosUids': firebase().firestore.FieldValue.arrayUnion( userRef.id )
                });

                // Siembra POR ROL (spec 2026-10-06 Fase 0.2, lib/role_seed):
                // cliente → plantilla de cliente; con alcance u otro rol →
                // permisos del rol; root y personal global → ninguna (su rol
                // es el valor por defecto en la empresa). Solo si NO existe:
                // una matriz previa (a mano o una reducción) se respeta.
                const role = applyUserScope( rolSnap.data() || {}, userData );
                const seed = seedMatrixFor({ role, isAdmin: false, roleId: rolRef.id });
                if (!matrixSnap.exists && seed) {
                  batch.set(minimalMatrixRef, {
                    user: userRef,
                    permissions: seed
                  });
                }

                return batch.commit().then(() => {
                  resolve(
                    dispatch({ type: CUSTOMERS.UPDATE.UPDATED_CUSTOMERS, data: userRef })
                  )
                }).catch(
                  error => reject(
                    dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: error.code, message: error.message })
                  )
                );
              } resolve();
            }).catch(
              error => reject(
                error && error.handled ? error : dispatch({ type: CUSTOMERS.CREATE.ERROR, code: error.code, message: error.message })
              )
            )

          } else {
            reject({ code: 'data', message: 'El usuario no esta registrado' });
          }
        }).catch(
          error => reject(
            dispatch({ type: CUSTOMERS.CREATE.ERROR, code: error.code, message: error.message })
          )
        )
        
      }  else {
        reject(
          dispatch({ type: CUSTOMERS.CREATE.ERROR, code: 'permissions', message: 'Permisos insuficientes para leer la lista de usuarios' })
        );
      }
    })

  },

  /**
   * Función que permite crear un cliente en el sistema.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * NIT del cliente que se va a crear
   * Razon social del cliente que se va a crear
   */
  create: ({ roles, nit, name }) => ( dispatch, state, firebase ) => {

    dispatch({ type: CUSTOMERS.CREATE.CREATING_CUSTOMERS });
  
    return new Promise( ( resolve, reject ) => {
      if ( roles.create ) {
  
        const db = firebase().firestore();
        const uid = firebase().auth().currentUser.uid;
        const batch = db.batch();

        const log_audit = db.collection('audit').doc();
        const customer_ref = db.collection('customers').doc();

        const after = { "NIT": nit, "Razon social": name }

        batch.set(log_audit, { uid, eventType: CUSTOMERS.CREATE.CREATE_CUSTOMERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
        batch.set(log_audit.collection('after').doc(), after);
        batch.set(customer_ref, after);

        batch.commit().then(() => {
          resolve(
            dispatch({ type: CUSTOMERS.CREATE.CREATED_CUSTOMERS, data: customer_ref })
          )
        }).catch(
          error => reject(
            dispatch({ type: CUSTOMERS.CREATE.ERROR, code: error.code, message: error.message })
          )
        );
    
      } else {
        reject(
          dispatch({ type: CUSTOMERS.CREATE.ERROR, code: 0, message: 'Permisos insuficientes para crear clientes' })
        );
      }
    })

  },
  
  /**
   * Función que permite consultar la lista de clientes.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * El filtro que se desea aplicar a la consulta
   */
  read: ({ roles, filter }) => ( dispatch, state, firebase ) => {
    
    dispatch({ type: CUSTOMERS.READ.READING_CUSTOMERS });
    
    return new Promise( ( resolve, reject ) => {
      if ( roles.read ) {

        filter = filter ? filter.value.trim().length > 0 ? filter : undefined : undefined

        const db = firebase().firestore();
        const customers = db.collection('customers');
        const documents = filter ? customers.where( filter.field, '==', filter.value ) : customers

        documents.get()
          .then( query => {

              const res = query.docs.map( doc => {

                const data = doc.data();
                const roles = state().get('roles').get('roles').roles.documents;
                
                const res = roles.level === 'all'
                  ? { id: doc.id, data: data }
                  : Array.isArray( data['Usuarios'] )
                    ? data['Usuarios'].filter( usr => usr.reference.id === firebase().auth().currentUser.uid ).length > 0
                      ? { id: doc.id, data: data }
                      : {}
                    : {}
                
                if ( Object.getOwnPropertyNames(res).length > 0 ) return res; else return undefined;

              });

              resolve( dispatch({ type: CUSTOMERS.READ.READED_CUSTOMERS, customers: res.filter( item => item !== undefined ) }) )

          }).catch( error => reject(
            dispatch({ type: CUSTOMERS.READ.ERROR, code: error.code, message: error.message })
          ))
    
      } else {
        reject(
          dispatch({ type: CUSTOMERS.READ.ERROR, code: 0, message: 'Permisos insuficientes para consultar la lista de clientes' })
        )
      }
    })
    
  },
  
  /**
   * Función que permite actualizar la información de un cliente.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * Los nuevos datos del cliente
   */
  update: ({ roles, data }) => ( dispatch, state, firebase ) => {
  
    dispatch({ type: CUSTOMERS.UPDATE.UPDATING_CUSTOMERS });
  
    return new Promise( async ( resolve, reject ) => {
      if ( roles.update ) {
    
        const db = firebase().firestore();
        const uid = firebase().auth().currentUser.uid;
        const batch = db.batch();

        const log_audit = db.collection('audit').doc();
        const customer_ref = db.collection('customers').doc(data.id);

        const before = (await customer_ref.get()).data();
        const after = { ...before, ...data.fields }

        batch.set(log_audit, { uid, eventType: CUSTOMERS.UPDATE.UPDATE_CUSTOMERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
        batch.set(log_audit.collection('before').doc(), before);
        batch.set(log_audit.collection('after').doc(), after);
        batch.update(customer_ref, { ...data.fields });

        return batch.commit().then(() => {
          resolve(
            dispatch({ type: CUSTOMERS.UPDATE.UPDATED_CUSTOMERS, data: { id: data.id } })
          )
        }).catch(
          error => reject(
            dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: error.code, message: error.message })
          )
        );

      } else {
        reject(
          dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: 0, message: 'Permisos insuficientes para actualizar la lista de clientes' })
        )
      }
    });
    
  },
  
  /**
   * Función que permite desvincular un usuario de la lista de clientes.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * ID del cliente
   * Email del usuario que se va a desvincular
   */
  removeUser: ({ roles, id, email }) => ( dispatch, state, firebase ) => {
    
    dispatch({ type: CUSTOMERS.DELETE.DELETING_CUSTOMERS });

    return new Promise( async ( resolve, reject ) => {
      if ( roles.delete ) {
      
        const db = firebase().firestore();
        const customer = db.collection('customers').doc( id );

        const uid = firebase().auth().currentUser.uid;
        const batch = db.batch();
        const log_audit = db.collection('audit').doc();

        const before = (await customer.get()).data();

        const data = Object.assign([], before?.['Usuarios']);
        const user = data.find( user => user?.email === email );
        // Si no está, no hay nada que quitar. Antes indexOf(undefined) = -1 y
        // splice(-1, 1) borraba el ÚLTIMO usuario del audit.
        if ( !user ) return resolve( dispatch({ type: CUSTOMERS.UPDATE.UPDATED_CUSTOMERS, data: { id } }) );

        const after = { ...before, 'Usuarios': data.filter( u => u !== user ) };

        batch.set(log_audit, { uid, eventType: CUSTOMERS.UPDATE.UPDATE_CUSTOMERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
        batch.set(log_audit.collection('before').doc(), before);
        batch.set(log_audit.collection('after').doc(), after);
        batch.update(customer, {
          'Usuarios': firebase().firestore.FieldValue.arrayRemove(user),
          'UsuariosUids': firebase().firestore.FieldValue.arrayRemove( user.reference?.id || '' ),
          // Espejo de admins para las reglas (spec 2026-10-06 Fase 6).
          'AdminsUids': firebase().firestore.FieldValue.arrayRemove( user.reference?.id || '' ),
        });

        return batch.commit().then(() => {
          resolve(
            dispatch({ type: CUSTOMERS.UPDATE.UPDATED_CUSTOMERS, data: { id } })
          )
        }).catch(
          error => reject(
            dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: error.code, message: error.message })
          )
        );

      } else {
        reject(
          dispatch({ type: CUSTOMERS.DELETE.ERROR, code: 'permissions', message: 'Permisos insuficientes para desvincular usuarios' })
        );
      }
    })

  },

  /**
   * Función que permite asignar a un usuario vinculado a un cliente como administrador.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * ID del cliente
   * Información del cliente
   */
  setUserAdmin: ({ roles, id, item }) => ( dispatch, state, firebase ) => {

    dispatch({ type: CUSTOMERS.UPDATE.UPDATING_CUSTOMERS });

    return new Promise( ( resolve, reject ) => {
      if ( roles.update ) {
      
        const db = firebase().firestore();
        const customer = db.collection('customers').doc( id );

        customer.get().then( async doc => {

          const uid = firebase().auth().currentUser.uid;
          const batch = db.batch();
          const log_audit_customers = db.collection('audit').doc();
          const log_audit_permissions = db.collection('audit').doc();

          const before = doc.data();
          const t = toggleAdmin({ usuarios: before?.['Usuarios'], email: item.email });
          if ( !t || !t.uid ) {
            return reject(
              dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: 'data', message: 'El usuario no está vinculado a este cliente' })
            );
          }
          // Jerarquía (enmienda E2): marcar o quitar administrador reescribe la matriz de
          // empresa (ADMIN_MATRIX trae eliminar), así que exige rango ≥ 60 y mayor que
          // el del objetivo. Rol del objetivo ilegible → no se ajusta. Antes de escribir.
          const callerRole = callerRoleOf( state );
          const deny = ( code, message ) => {
            dispatch({ type: CUSTOMERS.UPDATE.ERROR, code, message });
            return reject( Object.assign( new Error( message ), { code } ) );
          };
          if ( !canToggleDelete( callerRole ) ) {
            return deny( 'rank', 'Solo contador, supervisora o root pueden marcar o quitar administradores de empresa.' );
          }
          const targetRole = await loadTargetRole({ db, uid: t.uid });
          if ( !targetRole ) {
            return deny( 'role-unreadable', 'No se pudo leer el rol de este usuario; no se puede ajustar.' );
          }
          if ( !canAdjust( callerRole, targetRole ) ) {
            return deny( 'rank', 'No puedes ajustar a alguien de tu mismo rango o superior' );
          }

          const adminsUids = adminsUidsOf( t.users );
          const after = { ...before, 'Usuarios': t.users, 'AdminsUids': adminsUids };

          const matrixRef = db.doc(`folders/${ id }/users/${ t.uid }`);
          const before_permissions = (await matrixRef.get()).data() || null;
          // Siembra por rol (lib/role_seed): al marcar admin → ADMIN_MATRIX
          // (completa, sin cerrar carpetas); al quitarlo → la del rol
          // (cliente: plantilla), no todo en false. Root y personal global:
          // null → la matriz existente no se toca.
          const role = await loadScopedRole( db, t.uid );
          const seed = seedMatrixFor({ role, isAdmin: t.isAdminNow });

          batch.set(log_audit_customers, { uid, eventType: CUSTOMERS.UPDATE.UPDATE_CUSTOMERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit_customers.collection('before').doc(), before);
          batch.set(log_audit_customers.collection('after').doc(), after);

          batch.update(customer, { 'Usuarios': t.users, 'AdminsUids': adminsUids });

          if ( seed ) {
            const after_permissions = { user: db.collection('users').doc( t.uid ), permissions: seed };
            batch.set(log_audit_permissions, {
              uid,
              eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_PERMISSIONS`,
              eventDate: firebase().firestore.FieldValue.serverTimestamp(),
              targetUid: t.uid,
              resourcePath: matrixRef.path,
              origin: 'empresa',
              action: before_permissions ? 'update' : 'create'
            });
            if (before_permissions) batch.set(log_audit_permissions.collection('before').doc(), before_permissions);
            batch.set(log_audit_permissions.collection('after').doc(), after_permissions);
            batch.set(matrixRef, after_permissions);
          }

          return batch.commit().then(() => {
            resolve(
              dispatch({ type: CUSTOMERS.UPDATE.UPDATED_CUSTOMERS, data: { id } })
            )
          }).catch(
            error => reject(
              dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: error.code, message: error.message })
            )
          );

        }).catch( error => reject(
          dispatch({ type: CUSTOMERS.UPDATE.ERROR, code: error.code, message: error.message })
        ))
      
      }
    })

  },

  /**
   * Función que permite eliminar un cliente de la lista de clientes.
   * Recibe como parametros:
   * Roles del usuario que esta realizando la operación
   * ID del cliente
   */
  delete: ({ roles, id }) => ( dispatch, state, firebase ) => {
  
    dispatch({ type: CUSTOMERS.DELETE.DELETING_CUSTOMERS });
  
    return new Promise( async ( resolve, reject ) => {
      if ( roles.delete ) {

        const db = firebase().firestore();
        const uid = firebase().auth().currentUser.uid;
        const batch = db.batch();

        const log_audit = db.collection('audit').doc();
        const customer_ref = db.collection('customers').doc(id);
        const before = (await customer_ref.get()).data();

        batch.set(log_audit, { uid, eventType: CUSTOMERS.DELETE.DELETE_CUSTOMERS, eventDate: firebase().firestore.FieldValue.serverTimestamp() });
        batch.set(log_audit.collection('before').doc(), before);
        batch.delete(customer_ref);
        batch.commit().then(() => {
          resolve(
            dispatch({ type: CUSTOMERS.DELETE.DELETED_CUSTOMERS, data: { id } })
          )
        }).catch(
          error => reject(
            dispatch({ type: CUSTOMERS.DELETE.ERROR, code: error.code, message: error.message })
          )
        );

      } else {
        reject(
          dispatch({ type: CUSTOMERS.DELETE.ERROR, code: 0, message: 'Permisos insuficientes para eliminar la lista de clientes' })
        )
      }
    })

  }

}